
import { GoogleGenAI, Type } from "@google/genai";
import { MetricData, AIInsight, MRRMovement, Customer } from "../types";

// Construct a context string from dynamic data
const getSystemContext = (
  metrics: MetricData, 
  mrrMovement: MRRMovement[], 
  topCustomers: Customer[]
) => {
  return `You are the AI Growth Consultant for 'SubScale', a SaaS subscription management platform.
  
  CURRENT BUSINESS SNAPSHOT:
  - MRR: $${metrics.mrr.toLocaleString()}
  - ARR: $${metrics.arr.toLocaleString()}
  - Active Customers: ${metrics.activeUsers.toLocaleString()}
  - Churn Rate: ${metrics.churnRate}%
  - Revenue Growth: ${metrics.revenueGrowth}%
  
  RECENT MRR MOVEMENT (Last 3 Months):
  ${JSON.stringify(mrrMovement.slice(-3))}
  
  TOP CUSTOMERS (Sample):
  ${JSON.stringify(topCustomers.slice(0, 3).map(c => ({ name: c.name, ltv: c.totalLtv })))}

  Your goal is to provide strategic, data-driven advice. 
  - Be concise and professional.
  - When asked about trends, refer to the MRR movement data.
  - When asked about customers, refer to the specific top customers.
  - If asked to draft content (emails, posts), do so in a professional SaaS tone.
  `;
};

// Comment: Create a fresh chat session with an on-demand GoogleGenAI instance
export const createChatSession = (metrics: MetricData, mrrMovement: MRRMovement[], customers: Customer[]) => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  // Fix: Upgraded to 'gemini-3-pro-preview' for advanced reasoning and strategic growth consulting
  return ai.chats.create({
    model: "gemini-3-pro-preview",
    config: {
      systemInstruction: getSystemContext(metrics, mrrMovement, customers),
      temperature: 0.7,
    },
  });
};

// Comment: Fetch AI insights using a fresh GoogleGenAI instance to ensure up-to-date credentials
export const getAIInsights = async (metrics: MetricData): Promise<AIInsight[]> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Analyze these SaaS metrics and provide 3 strategic growth insights. 
      Current Stats: 
      MRR: $${metrics.mrr}, 
      ARR: $${metrics.arr}, 
      Churn Rate: ${metrics.churnRate}%, 
      Active Users: ${metrics.activeUsers}, 
      Growth: ${metrics.revenueGrowth}%.`,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              description: { type: Type.STRING },
              impact: { type: Type.STRING, "enum": ["High", "Medium", "Low"] },
              action: { type: Type.STRING }
            },
            required: ["title", "description", "impact", "action"]
          }
        }
      }
    });

    // Fix: Access response.text as a property, ensuring it handles potential undefined
    const jsonStr = (response.text || "[]").trim();
    return JSON.parse(jsonStr);
  } catch (error) {
    console.error("Gemini Insight Error:", error);
    return [
      {
        title: "Data Connection Issue",
        description: "Unable to generate live insights at this moment.",
        impact: "Low",
        action: "Retry Later"
      }
    ];
  }
};
